import { FC, useCallback, useEffect, useRef } from 'react';
import AppleSignin from 'react-apple-signin-auth';
import ReactGA from 'react-ga4';
import { useDispatch } from 'react-redux';

import { Box, BoxProps, Flex, Text, useToast } from '@chakra-ui/react';
import FacebookLogin from '@greatsumini/react-facebook-login';
import Cookies from 'js-cookie';
import { parseJWT } from 'utils/JWTencode';
import { getParentOrigin } from 'utils/getParentOrigin';

import { useLazyGetUserQuery, useSignInAppleMutation, useSignInFBMutation, useSignInGoogleMutation } from 'api/auth';

import { setIsAuthicated, setUser } from 'store/slices/auth';

import { ReactComponent as AppleIcon } from 'icons/apple.svg';
import { ReactComponent as FbIcon } from 'icons/fb.svg';
import { ReactComponent as GoogleIcon } from 'icons/google.svg';

const fbAppId = '1075306005823517';
const appleClientId = 'com.clutchpoints.ClutchPoints2';
const googleClientId = '182717589161-edketn4i47i5dl5oms8oke6o9bodg7ep.apps.googleusercontent.com';

const SocialSignIn: FC<BoxProps> = (props) => {
  const toast = useToast();
  const isGoogleInitialized = useRef(false);
  const isPromptActive = useRef(false);
  const oauthButtonRef = useRef<HTMLDivElement>(null);
  const [getUserMutation] = useLazyGetUserQuery();
  const dispatch = useDispatch();
  const [signInApple] = useSignInAppleMutation();
  const [signInFB] = useSignInFBMutation();
  const [signInGoogle] = useSignInGoogleMutation();

  const handleCredentialResponse = useCallback(
    async (response: any) => {
      try {
        isPromptActive.current = false;
        const { data, error } = await signInGoogle({
          credential: response.credential,
        });
        if (!error) {
          Cookies.set('sbc_access_token', data.token, { secure: true, expires: 60 * 60 * 24 * 7 });
          ReactGA.event({
            category: 'User',
            action: 'Sign-in by social button success',
          });
          const userData = await getUserMutation();
          if (!userData.error) {
            dispatch(setIsAuthicated(!!userData.data));
            dispatch(setUser(userData.data));
            toast({
              duration: 3000,
              isClosable: true,
              position: 'bottom',
              render: ({ onClose }) => {
                return (
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    p="5px 18px !important"
                    backgroundColor="#111111"
                    borderRadius="10px"
                    gap="10px"
                    w="min-content"
                    whiteSpace="nowrap"
                    margin="0 auto 10px"
                    onClick={onClose}
                  >
                    <Box>Successfully signed in</Box>
                  </Flex>
                );
              },
            });
          }
        }
      } catch (error) {
        console.error('Google sign-in error:', error);
        isPromptActive.current = false;
      }
    },
    [signInGoogle, getUserMutation, dispatch, toast]
  );

  useEffect(() => {
    if (isGoogleInitialized.current) return;

    const loadGoogleScript = () => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleSignIn;
      document.body.appendChild(script);
    };

    const initializeGoogleSignIn = () => {
      if (!(window as any).google) return;

      (window as any).google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        use_fedcm_for_prompt: true,
      });

      isGoogleInitialized.current = true;

      // Automatic One Tap / FedCM on page load
      setTimeout(() => {
        if (isPromptActive.current) return;

        try {
          isPromptActive.current = true;
          (window as any).google.accounts.id.prompt((notification: any) => {
            if (notification.isDismissedMoment?.()) {
              console.debug('One Tap dismissed:', notification.getDismissedReason?.());
              isPromptActive.current = false;
            }
            if (notification.isSkippedMoment?.()) {
              console.debug('One Tap skipped');
              isPromptActive.current = false;
            }
            if (notification.isNotDisplayed?.()) {
              console.debug('One Tap not displayed:', notification.getNotDisplayedReason?.());
              isPromptActive.current = false;
            }
          });
        } catch (error) {
          console.debug('One Tap error:', error);
          isPromptActive.current = false;
        }
      }, 100);
    };

    loadGoogleScript();

    return () => {
      if ((window as any).google?.accounts?.id) {
        try {
          (window as any).google.accounts.id.cancel();
        } catch (e) {
          console.debug('Cancel error:', e);
        }
      }
      isGoogleInitialized.current = false;
      isPromptActive.current = false;
    };
  }, [handleCredentialResponse]);

  const openGooglePopup = useCallback(() => {
    if (!(window as any).google?.accounts?.id) {
      toast({
        duration: 3000,
        isClosable: true,
        position: 'bottom',
        render: ({ onClose }) => {
          return (
            <Flex
              alignItems="center"
              justifyContent="center"
              p="5px 18px !important"
              backgroundColor="#111111"
              borderRadius="10px"
              gap="10px"
              w="min-content"
              whiteSpace="nowrap"
              margin="0 auto 10px"
              onClick={onClose}
            >
              <Box>Google Sign-In is not available. Please refresh the page.</Box>
            </Flex>
          );
        },
      });
      return;
    }

    // Cancel any active One Tap prompt
    if (isPromptActive.current) {
      try {
        (window as any).google.accounts.id.cancel();
        isPromptActive.current = false;
      } catch (e) {
        console.debug('Cancel error:', e);
      }
    }

    try {
      if (!oauthButtonRef.current) return;

      oauthButtonRef.current.innerHTML = '';
      (window as any).google.accounts.id.renderButton(oauthButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
      });

      setTimeout(() => {
        const button = oauthButtonRef.current?.querySelector('div[role="button"]') as HTMLElement;
        if (button) {
          button.click();
        }
      }, 50);
    } catch (error) {
      console.error('OAuth popup error:', error);
      toast({
        duration: 3000,
        isClosable: true,
        position: 'bottom',
        render: ({ onClose }) => {
          return (
            <Flex
              alignItems="center"
              justifyContent="center"
              p="5px 18px !important"
              backgroundColor="#111111"
              borderRadius="10px"
              gap="10px"
              w="min-content"
              whiteSpace="nowrap"
              margin="0 auto 10px"
              onClick={onClose}
            >
              <Box>Something went wrong, please try again</Box>
            </Flex>
          );
        },
      });
    }
  }, [toast]);

  const onSuccess = useCallback(
    async (token: string) => {
      Cookies.set('sbc_access_token', token, { secure: true, expires: 60 * 60 * 24 * 7 });
      ReactGA.event({
        category: 'User',
        action: 'Sign-in by social button success',
      });
      const { data, error } = await getUserMutation();
      if (!error) {
        dispatch(setIsAuthicated(!!data));
        dispatch(setUser(data));
        toast({
          duration: 3000,
          isClosable: true,
          position: 'bottom',
          render: ({ onClose }) => {
            return (
              <Flex
                alignItems="center"
                justifyContent="center"
                p="5px 18px !important"
                backgroundColor="#111111"
                borderRadius="10px"
                gap="10px"
                w="min-content"
                whiteSpace="nowrap"
                margin="0 auto 10px"
                onClick={onClose}
              >
                <Box>Successfully signed in</Box>
              </Flex>
            );
          },
        });
      }
    },
    [getUserMutation, dispatch, toast]
  );

  return (
    <Box {...props}>
      <Box>
        <Text textAlign="center" variant="text1">
          or quick access with
        </Text>
        <Box ref={oauthButtonRef} display="none" />
        <Flex mt="16px" justify="center" gap="16px">
          <GoogleIcon
            width="54px"
            height="54px"
            data-cy="google-auth-button"
            cursor="pointer"
            onClick={openGooglePopup}
          />

          <AppleSignin
            data-cy="apple-auth-button"
            uiType="light"
            authOptions={{
              clientId: appleClientId,
              scope: 'email name',
              redirectURI: getParentOrigin() + '/',
              state: 'state',
              nonce: 'nonce',
              usePopup: true,
            }}
            noDefaultStyle={false}
            onSuccess={async (response?: any) => {
              const appleData = parseJWT(response.authorization.id_token);
              const { data, error } = await signInApple({
                id: appleData.payload.sub,
                identityToken: response.authorization.id_token,
              });
              if (!error) {
                onSuccess(data.accessToken);
              }
            }}
            onError={(error?: Error) => {
              toast({
                duration: 3000,
                isClosable: true,
                position: 'bottom',
                render: ({ onClose }) => {
                  return (
                    <Flex
                      alignItems="center"
                      justifyContent="center"
                      p="5px 18px !important"
                      backgroundColor="#111111"
                      borderRadius="10px"
                      gap="10px"
                      w="min-content"
                      whiteSpace="nowrap"
                      margin="0 auto 10px"
                      onClick={onClose}
                    >
                      <Box>Something went wrong, please try again</Box>
                    </Flex>
                  );
                },
              });
              console.error(error);
            }}
            render={(props: any) => <AppleIcon cursor="pointer" width="54px" height="54px" {...props} />}
          />

          <FacebookLogin
            appId={fbAppId}
            autoLoad={false}
            fields="name,email,picture"
            onSuccess={async (response) => {
              const { data, error } = await signInFB({
                accessToken: response.accessToken,
                facebookId: response.userID,
              });
              if (!error) {
                onSuccess(data.accessToken);
              }
            }}
            onFail={(error) => {
              toast({
                duration: 3000,
                isClosable: true,
                position: 'bottom',
                render: ({ onClose }) => {
                  return (
                    <Flex
                      alignItems="center"
                      justifyContent="center"
                      p="5px 18px !important"
                      backgroundColor="#111111"
                      borderRadius="10px"
                      gap="10px"
                      w="min-content"
                      whiteSpace="nowrap"
                      margin="0 auto 10px"
                      onClick={onClose}
                    >
                      <Box>Something went wrong, please try again</Box>
                    </Flex>
                  );
                },
              });
              console.error('Login Failed!', error);
            }}
            render={(renderProps) => (
              <FbIcon
                cursor="pointer"
                data-cy="facebook-auth-button"
                width="54px"
                height="54px"
                onClick={renderProps.onClick}
              />
            )}
          />
        </Flex>
      </Box>
    </Box>
  );
};

export default SocialSignIn;
